import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { Trait, TraitsForContext } from '../types';

const parseBooleanTrait = (trait: Trait | undefined): boolean => {
	if (!trait) {
		return false;
	}
	if (typeof trait.value === 'boolean') {
		return trait.value;
	}
	if (typeof trait.value === 'string') {
		return trait.value === 'true';
	}
	return false;
};

export const getSiteTraitValues = (traits: Trait[]): TraitsForContext => {
	if (traits.length === 0) {
		return {
			automationUsage: 0,
			lastAutomationFreeLimitBreachDate: null,
			isSiteEligibleToServcoUplift: false,
			isEligibleForJsmStandardToPremium: false,
			isEligibleForJswStandardToPremium: false,
			isEligibleForJpdStandardToPremium: false,
			isEligibleForConfluenceStandardToPremium: false,
			isEligibleForJsmStandardToPremiumV2: false,
			isEligibleForJswStandardToPremiumV2: false,
			isEligibleForJpdStandardToPremiumV2: false,
			isEligibleForConfluenceStandardToPremiumV2: false,
			isEligibleForJswPremiumToTwc: false,
			isEligibleForConfluencePremiumToTwc: false,
			isEligibleForJswPremiumToTwcV2: false,
			isEligibleForConfluencePremiumToTwcV2: false,
			hasDeactivatedJiraConfluenceLoom: false,
		};
	}

	const automationUsageTrait = traits.find(
		(trait) => trait.name === 'growth_automation_limits_current_usage',
	);
	const lastAutomationFreeLimitBreachDateTrait = traits.find(
		(trait) => trait.name === 'growth_automation_limits_usage_latest_breach_date',
	);

	const servcoUpliftTrait = traits.find(
		(trait) => trait.name === 'is_site_eligible_to_servco_uplift',
	);

	const jsmUpsellTrait = traits.find(
		(trait) => trait.name === 'ea_jsm_is_eligible_for_standard_to_premium',
	);
	const jswUpsellTrait = traits.find(
		(trait) => trait.name === 'ea_jsw_is_eligible_for_standard_to_premium',
	);
	const jpdUpsellTrait = traits.find(
		(trait) => trait.name === 'ea_jpd_is_eligible_for_standard_to_premium',
	);
	const confluenceUpsellTrait = traits.find(
		(trait) => trait.name === 'ea_confluence_is_eligible_for_standard_to_premium',
	);

	const jsmUpsellTraitV2 = traits.find(
		(trait) => trait.name === 'ea_jsm_is_eligible_for_standard_to_premium_v2',
	);
	const jswUpsellTraitV2 = traits.find(
		(trait) => trait.name === 'ea_jsw_is_eligible_for_standard_to_premium_v2',
	);
	const jpdUpsellTraitV2 = traits.find(
		(trait) => trait.name === 'ea_jpd_is_eligible_for_standard_to_premium_v2',
	);
	const confluenceUpsellTraitV2 = traits.find(
		(trait) => trait.name === 'ea_confluence_is_eligible_for_standard_to_premium_v2',
	);

	const jswPremiumToTwcTrait = traits.find(
		(trait) => trait.name === 'ea_jsw_is_eligible_for_premium_to_twc',
	);
	const confluencePremiumToTwcTrait = traits.find(
		(trait) => trait.name === 'ea_confluence_is_eligible_for_premium_to_twc',
	);
	const hasDeactivatedJiraConfluenceLoomTrait = traits.find(
		(trait) => trait.name === 'site_has_deactivated_jira_confluence_loom',
	);

	const jswPremiumToTwcTraitV2 = traits.find(
		(trait) => trait.name === 'ea_jsw_is_eligible_for_premium_to_twc_v2',
	);
	const confluencePremiumToTwcTraitV2 = traits.find(
		(trait) => trait.name === 'ea_confluence_is_eligible_for_premium_to_twc_v2',
	);

	const automationUsage =
		automationUsageTrait &&
		automationUsageTrait.value !== undefined &&
		!isNaN(Number(automationUsageTrait.value))
			? Number(automationUsageTrait.value)
			: 0;

	const lastAutomationFreeLimitBreachDate =
		lastAutomationFreeLimitBreachDateTrait &&
		typeof lastAutomationFreeLimitBreachDateTrait.value === 'string' &&
		!isNaN(Date.parse(lastAutomationFreeLimitBreachDateTrait.value))
			? new Date(lastAutomationFreeLimitBreachDateTrait.value)
			: null;

	const isSiteEligibleToServcoUplift = Boolean(
		servcoUpliftTrait && typeof servcoUpliftTrait.value === 'boolean' && servcoUpliftTrait.value,
	);

	const useNewUpsellTraits = fg('ea_upsell_new_traits');
	const useNewPremiumToTwcTraits = fg('ea_twc_upsell_new_traits');

	return {
		automationUsage,
		lastAutomationFreeLimitBreachDate,
		isSiteEligibleToServcoUplift,
		isEligibleForJsmStandardToPremium: parseBooleanTrait(
			useNewUpsellTraits ? jsmUpsellTraitV2 : jsmUpsellTrait,
		),
		isEligibleForJswStandardToPremium: parseBooleanTrait(
			useNewUpsellTraits ? jswUpsellTraitV2 : jswUpsellTrait,
		),
		isEligibleForJpdStandardToPremium: parseBooleanTrait(
			useNewUpsellTraits ? jpdUpsellTraitV2 : jpdUpsellTrait,
		),
		isEligibleForConfluenceStandardToPremium: parseBooleanTrait(
			useNewUpsellTraits ? confluenceUpsellTraitV2 : confluenceUpsellTrait,
		),
		isEligibleForJsmStandardToPremiumV2: parseBooleanTrait(jsmUpsellTraitV2),
		isEligibleForJswStandardToPremiumV2: parseBooleanTrait(jswUpsellTraitV2),
		isEligibleForJpdStandardToPremiumV2: parseBooleanTrait(jpdUpsellTraitV2),
		isEligibleForConfluenceStandardToPremiumV2: parseBooleanTrait(confluenceUpsellTraitV2),
		isEligibleForJswPremiumToTwc: parseBooleanTrait(
			useNewPremiumToTwcTraits ? jswPremiumToTwcTraitV2 : jswPremiumToTwcTrait,
		),
		isEligibleForConfluencePremiumToTwc: parseBooleanTrait(
			useNewPremiumToTwcTraits ? confluencePremiumToTwcTraitV2 : confluencePremiumToTwcTrait,
		),
		isEligibleForJswPremiumToTwcV2: parseBooleanTrait(jswPremiumToTwcTraitV2),
		isEligibleForConfluencePremiumToTwcV2: parseBooleanTrait(confluencePremiumToTwcTraitV2),
		hasDeactivatedJiraConfluenceLoom: parseBooleanTrait(hasDeactivatedJiraConfluenceLoomTrait),
	};
};
